import React from 'react';
import {
  PieChart, Pie, Cell, Tooltip, ResponsiveContainer,
  XAxis, YAxis, CartesianGrid,
  BarChart, Bar, Legend, Area, AreaChart, LabelList,
} from 'recharts';
import {
  fatturatoPieData, discountLineData, quoteMercatoPieData,
  confrontoFormatiData, genZData, turnoverImpactData, ceoPrioritiesData,
} from '../../data/mockData';

/* ── Shared gradient defs ────────────────────────────────── */
const GradientDefs = () => (
  <defs>
    <linearGradient id="barOrange" x1="0" y1="0" x2="0" y2="1">
      <stop offset="0%" stopColor="#f59e0b" stopOpacity={0.95} />
      <stop offset="100%" stopColor="#e8690a" stopOpacity={1} />
    </linearGradient>
    <linearGradient id="barPurple" x1="0" y1="0" x2="0" y2="1">
      <stop offset="0%" stopColor="#a78bfa" stopOpacity={0.95} />
      <stop offset="100%" stopColor="#8b5cf6" stopOpacity={1} />
    </linearGradient>
    <linearGradient id="areaOrange" x1="0" y1="0" x2="0" y2="1">
      <stop offset="0%" stopColor="#e8690a" stopOpacity={0.2} />
      <stop offset="100%" stopColor="#e8690a" stopOpacity={0.01} />
    </linearGradient>
    <linearGradient id="barHorizontalOrange" x1="0" y1="0" x2="1" y2="0">
      <stop offset="0%" stopColor="#e8690a" stopOpacity={1} />
      <stop offset="100%" stopColor="#f59e0b" stopOpacity={0.9} />
    </linearGradient>
  </defs>
);

/* ── Custom tooltip ──────────────────────────────────────── */
const CustomTooltip = ({ active, payload, label, suffix = '' }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-lg border border-border bg-card px-3 py-2 shadow-lg">
      {label && <p className="text-xs font-semibold text-foreground font-display mb-1">{label}</p>}
      {payload.map((p, i) => (
        <p key={i} className="text-xs text-muted-foreground flex items-center gap-1.5">
          <span className="inline-block h-2 w-2 rounded-full" style={{ background: p.color || p.payload?.fill || p.fill }} />
          <span className="font-medium">{p.name || p.dataKey}:</span>
          <span className="font-semibold text-foreground">{p.value}{suffix}</span>
        </p>
      ))}
    </div>
  );
};

/* ── Custom legend for bar charts ────────────────────────── */
const gradientColorMap = {
  'url(#barOrange)': '#e8690a',
  'url(#barPurple)': '#8b5cf6',
  'url(#barHorizontalOrange)': '#e8690a',
};

const resolveColor = (color) => gradientColorMap[color] || color;

const CustomLegend = ({ payload }) => (
  <div className="flex items-center justify-center gap-5 mt-1">
    {payload?.map((entry, i) => (
      <div key={i} className="flex items-center gap-1.5">
        <span className="inline-block h-2.5 w-2.5 rounded-sm flex-shrink-0" style={{ backgroundColor: resolveColor(entry.color) }} />
        <span className="text-[11px] text-muted-foreground font-medium">{entry.value}</span>
      </div>
    ))}
  </div>
);

/* ── Pie legend (below donut) ────────────────────────────── */
const PieLegend = ({ data }) => (
  <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-1.5 mt-2">
    {data.map((entry, i) => (
      <div key={i} className="flex items-center gap-1.5">
        <span className="inline-block h-2.5 w-2.5 rounded-full flex-shrink-0" style={{ background: entry.fill }} />
        <span className="text-[10px] text-muted-foreground font-medium">{entry.name} ({entry.value}%)</span>
      </div>
    ))}
  </div>
);

/* ── Chart wrapper ───────────────────────────────────────── */
const ChartWrapper = ({ title, subtitle, children, height = 220 }) => (
  <div className="chart-container my-6 rounded-xl border border-border bg-card p-5">
    <h4 className="text-sm font-bold text-foreground font-display mb-1">{title}</h4>
    {subtitle && <p className="text-xs text-muted-foreground mb-4">{subtitle}</p>}
    <div style={{ width: '100%', height }}>{children}</div>
  </div>
);

/* ── Axis shared props — NO axis lines, NO tick lines ────── */
const xAxisClean = {
  tick: { fontSize: 11, fill: '#94a3b8' },
  axisLine: false,
  tickLine: false,
};

const yAxisClean = {
  tick: { fontSize: 11, fill: '#94a3b8' },
  axisLine: false,
  tickLine: false,
};

/* ═══════════════════════════════════════════════════════════
   1. FATTURATO DONUT — No labels, legend only
   ═══════════════════════════════════════════════════════════ */
export const FatturatoPieChart = () => (
  <ChartWrapper title="Distribuzione fatturato GDO" subtitle="Costo del lavoro vs Margine EBIT (% fatturato)" height={230}>
    <ResponsiveContainer width="100%" height={185}>
      <PieChart>
        <Pie
          data={fatturatoPieData}
          cx="50%" cy="50%"
          innerRadius={48} outerRadius={78}
          dataKey="value"
          paddingAngle={2}
          animationBegin={200}
          animationDuration={1000}
          stroke="none"
        >
          {fatturatoPieData.map((entry, i) => (
            <Cell key={i} fill={entry.fill} />
          ))}
        </Pie>
        <Tooltip content={<CustomTooltip suffix="%" />} />
      </PieChart>
    </ResponsiveContainer>
    <PieLegend data={fatturatoPieData} />
  </ChartWrapper>
);

/* ═══════════════════════════════════════════════════════════
   2. DISCOUNT AREA CHART — Clean axes, no border lines
   ═══════════════════════════════════════════════════════════ */
export const DiscountLineChart = () => (
  <ChartWrapper title="Evoluzione quota Discount (%)" subtitle="2010–2030 (proiezione)">
    <ResponsiveContainer>
      <AreaChart data={discountLineData} margin={{ top: 10, right: 10, bottom: 0, left: 0 }}>
        <GradientDefs />
        <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
        <XAxis dataKey="anno" {...xAxisClean} />
        <YAxis {...yAxisClean} domain={[10, 30]} tickFormatter={(v) => `${v}%`} />
        <Tooltip content={<CustomTooltip suffix="%" />} />
        <Area
          type="monotone" dataKey="quota"
          stroke="#e8690a" strokeWidth={2.5}
          fill="url(#areaOrange)"
          dot={{ fill: '#fff', stroke: '#e8690a', strokeWidth: 2, r: 4 }}
          activeDot={{ fill: '#e8690a', stroke: '#fff', strokeWidth: 2, r: 6 }}
          animationDuration={1200}
        />
      </AreaChart>
    </ResponsiveContainer>
  </ChartWrapper>
);

/* ═══════════════════════════════════════════════════════════
   3. QUOTE MERCATO DONUT — No labels, legend only
   ═══════════════════════════════════════════════════════════ */
export const QuoteMercatoPieChart = () => (
  <ChartWrapper title="Quote di mercato principali operatori" subtitle="% (stime 2024)" height={280}>
    <ResponsiveContainer width="100%" height={210}>
      <PieChart>
        <Pie
          data={quoteMercatoPieData}
          cx="50%" cy="50%"
          innerRadius={50} outerRadius={85}
          dataKey="value"
          paddingAngle={1.5}
          animationBegin={300}
          animationDuration={1000}
          stroke="none"
        >
          {quoteMercatoPieData.map((entry, i) => (
            <Cell key={i} fill={entry.fill} />
          ))}
        </Pie>
        <Tooltip content={<CustomTooltip suffix="%" />} />
      </PieChart>
    </ResponsiveContainer>
    <PieLegend data={quoteMercatoPieData} />
  </ChartWrapper>
);

/* ═══════════════════════════════════════════════════════════
   4. CONFRONTO FORMATI — Clean bars, no axis borders
   ═══════════════════════════════════════════════════════════ */
export const ConfrontoFormatiChart = () => (
  <ChartWrapper title="Discount vs Tradizionale" subtitle="Margine EBIT e ROI a confronto" height={220}>
    <ResponsiveContainer>
      <BarChart data={confrontoFormatiData} barGap={8} barCategoryGap="30%" margin={{ top: 20, right: 10, bottom: 0, left: 0 }}>
        <GradientDefs />
        <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
        <XAxis dataKey="name" {...xAxisClean} />
        <YAxis {...yAxisClean} tickFormatter={(v) => `${v}%`} />
        <Tooltip content={<CustomTooltip suffix="%" />} />
        <Legend content={<CustomLegend />} />
        <Bar dataKey="Discount" fill="url(#barOrange)" radius={[6, 6, 0, 0]} animationDuration={800}>
          <LabelList dataKey="Discount" position="top" formatter={(v) => `${v}%`} style={{ fontSize: 11, fontWeight: 700, fill: '#e8690a' }} />
        </Bar>
        <Bar dataKey="Tradizionale" fill="url(#barPurple)" radius={[6, 6, 0, 0]} animationDuration={800}>
          <LabelList dataKey="Tradizionale" position="top" formatter={(v) => `${v}%`} style={{ fontSize: 11, fontWeight: 700, fill: '#8b5cf6' }} />
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  </ChartWrapper>
);

/* ═══════════════════════════════════════════════════════════
   5. GEN Z — Individual colors, no axis borders
   ═══════════════════════════════════════════════════════════ */
const genZColors = ['#e8690a', '#8b5cf6', '#f59e0b'];

export const GenZChart = () => (
  <ChartWrapper title="Aspettative Gen Z & Millennials" subtitle="% rispondenti" height={200}>
    <ResponsiveContainer>
      <BarChart data={genZData} barCategoryGap="25%" margin={{ top: 20, right: 10, bottom: 0, left: 0 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
        <XAxis dataKey="name" {...xAxisClean} />
        <YAxis {...yAxisClean} tickFormatter={(v) => `${v}%`} domain={[0, 100]} />
        <Tooltip content={<CustomTooltip suffix="%" />} />
        <Bar dataKey="value" radius={[6, 6, 0, 0]} animationDuration={900}>
          {genZData.map((entry, i) => (
            <Cell key={i} fill={genZColors[i % genZColors.length]} />
          ))}
          <LabelList dataKey="value" position="top" formatter={(v) => `${v}%`} style={{ fontSize: 12, fontWeight: 700, fill: '#334155' }} />
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  </ChartWrapper>
);

/* ═══════════════════════════════════════════════════════════
   6. TURNOVER IMPACT — Gradient bars, no axis borders
   ═══════════════════════════════════════════════════════════ */
export const TurnoverImpactChart = () => (
  <ChartWrapper title="Impatto turnover sull'EBIT" subtitle="Scenario conservativo vs elevato" height={220}>
    <ResponsiveContainer>
      <BarChart data={turnoverImpactData} barGap={8} barCategoryGap="30%" margin={{ top: 20, right: 10, bottom: 0, left: 0 }}>
        <GradientDefs />
        <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
        <XAxis dataKey="name" {...xAxisClean} />
        <YAxis {...yAxisClean} domain={[0, 130]} />
        <Tooltip content={<CustomTooltip />} />
        <Legend content={<CustomLegend />} />
        <Bar dataKey="costo" name="Costo (€ mln)" fill="url(#barOrange)" radius={[6, 6, 0, 0]} animationDuration={800}>
          <LabelList dataKey="costo" position="top" formatter={(v) => `€${v}`} style={{ fontSize: 11, fontWeight: 700, fill: '#e8690a' }} />
        </Bar>
        <Bar dataKey="ebit" name="% EBIT eroso" fill="url(#barPurple)" radius={[6, 6, 0, 0]} animationDuration={800}>
          <LabelList dataKey="ebit" position="top" formatter={(v) => `${v}%`} style={{ fontSize: 11, fontWeight: 700, fill: '#8b5cf6' }} />
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  </ChartWrapper>
);

/* ═══════════════════════════════════════════════════════════
   7. CEO PRIORITIES — Horizontal gradient bars, no axis borders
   ═══════════════════════════════════════════════════════════ */
export const CeoPrioritiesChart = () => (
  <ChartWrapper title="Priorità strategiche CEO GDO" subtitle="% operatori GDO (Mediobanca 2025)" height={240}>
    <ResponsiveContainer>
      <BarChart data={ceoPrioritiesData} layout="vertical" barCategoryGap="20%" margin={{ top: 5, right: 40, bottom: 5, left: 5 }}>
        <GradientDefs />
        <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" horizontal={false} />
        <XAxis type="number" {...xAxisClean} domain={[0, 100]} tickFormatter={(v) => `${v}%`} />
        <YAxis type="category" dataKey="name" {...yAxisClean} width={110} tick={{ fontSize: 10, fill: '#94a3b8' }} />
        <Tooltip content={<CustomTooltip suffix="%" />} />
        <Bar dataKey="value" fill="url(#barHorizontalOrange)" radius={[0, 6, 6, 0]} animationDuration={1000}>
          <LabelList dataKey="value" position="right" formatter={(v) => `${v}%`} style={{ fontSize: 11, fontWeight: 700, fill: '#334155' }} />
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  </ChartWrapper>
);
